import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  Badge,
  useDisclosure,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  InputGroup,
  InputLeftElement,
  Input
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserInbox, fetchSentMessages } from '../../Redux/Login/action';
import UserMessagesModal from './UserMessagesModal';
import AdminMessagesModal from './AdminMessagesModal';
import SendMessageModal from './SendMessageModal';
import SentMessagesModal from './SentMessagesModal';
import { EmailIcon, ChatIcon, BellIcon, SearchIcon, AddIcon } from '@chakra-ui/icons';
import ConversationModal from './ConversationModal';
import { useVisibilityAwareInterval } from '../../hooks/useTabVisibility';
import BurrowSpinner from '../../componants/UI/BurrowSpinner';
import BurrowAvatar from '../../componants/UI/BurrowAvatar';

// ConversationCard component to mirror UserCard styling
const ConversationCard = ({ conversation, onClick }) => {
  const partnerName = conversation?.burrowerId || "Unknown User";
  const partnerAvatar = conversation?.partnerAvatar || "/media/media-for-app/default-avatar.png";
  const lastMessage = conversation?.lastMessage?.userMessageContents || "No messages";
  const hasUnread = conversation?.hasUnread || false;
  const messageCount = conversation?.messageCount || 0;
  const unreadCount = conversation?.messages?.filter(msg => !msg.isRead)?.length || 0;
  
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const intervals = {
      year: 31536000, month: 2592000, week: 604800,
      day: 86400, hour: 3600, minute: 60, second: 1
    };
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const counter = Math.floor(seconds / secondsInUnit);
      if (counter > 0) return counter === 1 ? `1${unit.charAt(0)}` : `${counter}${unit.charAt(0)}`;
    }
    return 'now';
  };
  
  const timeAgo = formatTimeAgo(conversation?.lastMessage?.sentAt);
  
  return (
    <Flex
      py={3}
      px={4}
      align="center"
      gap={3}
      cursor="pointer"
      bg="transparent"
      transition="background 0.1s ease"
      _hover={{ bg: "whiteAlpha.50" }}
      onClick={onClick}
      width="100%"
      position="relative"
      _after={{
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "8%",
        right: "8%",
        height: "1px",
        background: "linear-gradient(90deg, transparent, rgba(236,201,75,0.25) 30%, rgba(236,201,75,0.25) 70%, transparent)",
        boxShadow: "0 1px 4px rgba(236,201,75,0.12)",
      }}
    >
      {/* Avatar with unread badge */}
      <Box position="relative" flexShrink={0}>
        <BurrowAvatar 
          size="md" 
          name={partnerName} 
          src={partnerAvatar}
        />
        {hasUnread && unreadCount > 0 && (
          <Badge 
            colorScheme="yellow" 
            position="absolute"
            top="-4px"
            right="-4px"
            borderRadius="full"
            fontSize="2xs"
            minW="18px"
            h="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            border="2px solid"
            borderColor="gray.800"
          >
            {unreadCount}
          </Badge>
        )}
      </Box>

      {/* Name + message preview */}
      <Box flex={1} minW={0} overflow="hidden">
        <Text 
          fontWeight={hasUnread ? "bold" : "semibold"}
          fontSize="sm"
          color="white"
          isTruncated
          mb={0.5}
        >
          {partnerName}
        </Text>
        <Text 
          fontSize="xs" 
          color={hasUnread ? "gray.200" : "gray.400"}
          noOfLines={1}
          fontWeight={hasUnread ? "medium" : "normal"}
        >
          {lastMessage}
        </Text>
      </Box>

      {/* Time + count */}
      <VStack spacing={0} flexShrink={0} align="flex-end">
        <Text 
          fontSize="xs" 
          color={hasUnread ? "yellow.300" : "gray.500"}
          fontWeight={hasUnread ? "bold" : "normal"}
        >
          {timeAgo}
        </Text>
        <Text fontSize="2xs" color="gray.500">
          {messageCount} msg{messageCount !== 1 ? 's' : ''}
        </Text>
      </VStack>
    </Flex>
  );
};

const MessagesTab = () => {
  const dispatch = useDispatch();
  const { 
    userMessages, 
    adminMessages, 
    sentMessages,
    unreadUserMessages, 
    unreadAdminMessages,
    loading, 
    sentMessagesLoading,
    error, 
    sentMessagesError
  } = useSelector(state => state.auth.messages || {});
  
  const [tabIndex, setTabIndex] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  
  // Add state for selected conversation
  const [selectedConversation, setSelectedConversation] = useState(null);
  const conversationModal = useDisclosure();
  
  // Keep existing useDisclosure hooks for modals
  const userMessagesModal = useDisclosure();
  const adminMessagesModal = useDisclosure();
  const sendMessageModal = useDisclosure();
  const sentMessagesModal = useDisclosure();
  
  // Handle tab change
  const handleTabChange = (index) => {
    setTabIndex(index);
    
    // Open the corresponding modal based on tab selection
    if (index === 0) {
      userMessagesModal.onOpen();
    } else if (index === 1) {
      adminMessagesModal.onOpen();
    } else if (index === 2) {
      sentMessagesModal.onOpen();
    } else if (index === 3) {
      sendMessageModal.onOpen();
    }
  };

  // Load messages on component mount
  useEffect(() => {
    console.time('messages-tab-load');
    dispatch(fetchUserInbox());
    dispatch(fetchSentMessages());
    console.timeEnd('messages-tab-load');
  }, [dispatch]);
  
  // Set up periodic refresh with tab visibility awareness
  useVisibilityAwareInterval(() => {
    dispatch(fetchUserInbox());
    dispatch(fetchSentMessages());
  }, 60000, [dispatch]); // Refresh every minute when tab is visible
  
  // Close all modals when tab changes
  useEffect(() => {
    const closeAllModals = () => {
      userMessagesModal.onClose();
      adminMessagesModal.onClose();
      sentMessagesModal.onClose();
      sendMessageModal.onClose();
    };
    
    return () => closeAllModals();
  }, []);

  return (
    <Box p={{ base: 0, md: 4 }}>
      {loading ? (
        <Flex justify="center" align="center" minHeight="200px">
          <BurrowSpinner size="xl" />
        </Flex>
      ) : error ? (
        <Box p={4} bg="red.900" borderRadius="md" color="red.300">
          <Text>Error loading messages: {error}</Text>
          <Button 
            mt={2} 
            size="sm" 
            colorScheme="red" 
            onClick={() => {
              dispatch(fetchUserInbox());
              dispatch(fetchSentMessages());
            }}
          >
            Retry
          </Button>
        </Box>
      ) : (
        <Box width="100%">
          {/* Tabs Interface */}
          <Tabs variant="line" colorScheme="yellow">
              <TabList display="flex" width="100%">
                <Tab flex={1} px={1} py={3}>
                  <VStack spacing={1} align="center">
                    <Box position="relative" display="inline-block">
                      <ChatIcon boxSize={5} />
                      {unreadUserMessages > 0 && (
                        <Badge 
                          colorScheme="red" 
                          borderRadius="full" 
                          position="absolute" 
                          top="-6px" 
                          right="-12px" 
                          fontSize="2xs"
                          minW="16px"
                          h="16px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {unreadUserMessages}
                        </Badge>
                      )}
                    </Box>
                    <Text fontSize="xs" lineHeight="1">User</Text>
                  </VStack>
                </Tab>
                <Tab flex={1} px={1} py={3}>
                  <VStack spacing={1} align="center">
                    <Box position="relative" display="inline-block">
                      <BellIcon boxSize={5} />
                      {unreadAdminMessages > 0 && (
                        <Badge 
                          colorScheme="purple" 
                          borderRadius="full" 
                          position="absolute" 
                          top="-6px" 
                          right="-12px" 
                          fontSize="2xs"
                          minW="16px"
                          h="16px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {unreadAdminMessages}
                        </Badge>
                      )}
                    </Box>
                    <Text fontSize="xs" lineHeight="1">Important</Text>
                  </VStack>
                </Tab>
                <Tab flex={1} px={1} py={3}>
                  <VStack spacing={1} align="center">
                    <EmailIcon boxSize={5} />
                    <Text fontSize="xs" lineHeight="1">Sent</Text>
                  </VStack>
                </Tab>
                <Tab flex={1} px={1} py={3}>
                  <VStack spacing={1} align="center">
                    <AddIcon boxSize={5} />
                    <Text fontSize="xs" lineHeight="1">Compose</Text>
                  </VStack>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={{ base: 0, md: 4 }}>
                  <Flex 
                    direction={{ base: "column", md: "row" }}
                    justifyContent="space-between" 
                    mb={4}
                    gap={2}
                  >
                    <Text fontSize="lg" fontWeight="medium">User Messages</Text>
                    <InputGroup size="sm" maxW={{ base: "100%", md: "250px" }}>
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                      </InputLeftElement>
                      <Input 
                        placeholder="Search conversations..." 
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </InputGroup>
                  </Flex>
                  {userMessages?.length === 0 ? (
                    <Box textAlign="center" p={6} color="gray.500">
                      <ChatIcon boxSize={10} mb={2} />
                      <Text>You don't have any user messages</Text>
                    </Box>
                  ) : (
                    <VStack align="stretch" spacing={0}>
                      {(() => {
                        // Group messages by fromBurrowerId
                        const conversations = {};
                        userMessages.forEach(msg => {
                          if (!conversations[msg.fromBurrowerId]) {
                            conversations[msg.fromBurrowerId] = [];
                          }
                          conversations[msg.fromBurrowerId].push(msg);
                        });
                        
                        // Sort conversations by most recent message
                        const sortedConversations = Object.entries(conversations)
                          .map(([burrowerId, messages]) => {
                            // Sort messages by date (newest first)
                            const sortedMessages = [...messages].sort(
                              (a, b) => new Date(b.sentAt) - new Date(a.sentAt)
                            );
                            
                            // Check if conversation has unread messages
                            const hasUnread = sortedMessages.some(msg => !msg.isRead);
                            
                            // Get last message for preview
                            const lastMessage = sortedMessages[0];
                            
                            return {
                              burrowerId,
                              messages: sortedMessages,
                              lastMessage,
                              hasUnread,
                              messageCount: messages.length
                            };
                          })
                          .sort((a, b) => new Date(b.lastMessage.sentAt) - new Date(a.lastMessage.sentAt));
                          
                        // Filter by search input if any
                        const filteredConversations = searchInput 
                          ? sortedConversations.filter(conv => 
                              conv.burrowerId.toLowerCase().includes(searchInput.toLowerCase()) ||
                              conv.messages.some(msg => 
                                msg.userMessageContents.toLowerCase().includes(searchInput.toLowerCase())
                              )
                            )
                          : sortedConversations;                            // Return conversation cards
                        return filteredConversations.map(conv => {
                          // Extract any available avatar from the messages 
                          // Improved avatar detection - check multiple possible fields
                          let partnerAvatar;
                          const partnerMessage = conv.messages.find(msg => 
                            msg.fromBurrowerId === conv.burrowerId && 
                            (msg.senderAvatar || msg.userAvatar || msg.avatarUrl));
                          
                          if (partnerMessage) {
                            partnerAvatar = partnerMessage.senderAvatar || 
                                           partnerMessage.userAvatar || 
                                           partnerMessage.avatarUrl;
                          }
                            // If no avatar found, use the default
                          if (!partnerAvatar) {
                            partnerAvatar = "/media/media-for-app/default-avatar.png"; // Updated to local path
                          }
                            // Make sure the avatar is properly set on the conversation object
                          conv.partnerAvatar = partnerAvatar;
                          console.log("Setting conversation avatar:", partnerAvatar);
                          
                          return (
                          <ConversationCard
                            key={conv.burrowerId}
                            conversation={conv}
                            onClick={() => {
                              // Open conversation modal with these messages
                              // Find any sent messages to this user
                              const relevantSentMessages = sentMessages?.filter(
                                msg => msg.toBurrowerId === conv.burrowerId
                              ) || [];
                                // Ensure we have the partner's avatar
                              const partnerAvatar = conv.partnerAvatar || "/media/media-for-app/default-avatar.png";
                              console.log("Using partner avatar URL:", partnerAvatar);
                              
                              // Prepare sent messages to match the expected format
                              const formattedSentMessages = relevantSentMessages.map(msg => ({
                                ...msg,
                                fromBurrowerId: 'me', // Mark as from self
                                userMessageContents: msg.messageContents // Ensure content is accessible
                              }));
                              
                              // Combine received and sent messages
                              const allMessages = [
                                ...conv.messages,
                                ...formattedSentMessages
                              ].sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt)); // Sort by time
                              
                              // Make sure to include the partner's avatar
                              const selectedConversation = {
                                burrowerId: conv.burrowerId,
                                partnerAvatar: partnerAvatar,
                                messages: allMessages
                              };
                              
                              // Logging for debugging
                              console.log("Opening conversation with:", {
                                partner: conv.burrowerId,
                                avatar: partnerAvatar,
                                messageCount: allMessages.length
                              });
                              
                              setSelectedConversation(selectedConversation);
                              conversationModal.onOpen();
                            }}
                          />
                          );
                        });
                      })()}
                    </VStack>
                  )}
                </TabPanel>
                
                <TabPanel p={{ base: 0, md: 4 }}>
                  <Text fontSize="lg" fontWeight="medium" mb={4}>Important Messages</Text>
                  {adminMessages?.length === 0 ? (
                    <Box textAlign="center" p={6} color="gray.500">
                      <BellIcon boxSize={10} mb={2} />
                      <Text>You don't have any important messages</Text>
                    </Box>
                  ) : (
                    <VStack align="stretch" spacing={0}>
                      {adminMessages?.map(msg => {
                        const formatTime = (dateString) => {
                          if (!dateString) return '';
                          const date = new Date(dateString);
                          const now = new Date();
                          const seconds = Math.floor((now - date) / 1000);
                          const intervals = { year: 31536000, month: 2592000, week: 604800, day: 86400, hour: 3600, minute: 60, second: 1 };
                          for (const [unit, secondsInUnit] of Object.entries(intervals)) {
                            const counter = Math.floor(seconds / secondsInUnit);
                            if (counter > 0) return counter === 1 ? `1${unit.charAt(0)}` : `${counter}${unit.charAt(0)}`;
                          }
                          return 'now';
                        };
                        return (
                        <Flex 
                          key={msg.adminMessageId} 
                          py={3}
                          px={4}
                          align="center"
                          gap={3}
                          bg="transparent"
                          width="100%"
                          position="relative"
                          _after={{
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "8%",
                            right: "8%",
                            height: "1px",
                            background: "linear-gradient(90deg, transparent, rgba(236,201,75,0.25) 30%, rgba(236,201,75,0.25) 70%, transparent)",
                            boxShadow: "0 1px 4px rgba(236,201,75,0.12)",
                          }}
                        >
                          <Box position="relative" flexShrink={0}>
                            <BurrowAvatar size="md" name={msg.fromBurrowerId} />
                          </Box>
                          <Box flex={1} minW={0} overflow="hidden">
                            <Flex align="baseline" justify="space-between" mb={0.5}>
                              <Text 
                                fontWeight={!msg.isRead ? "bold" : "semibold"}
                                fontSize="15px"
                                color="white"
                                isTruncated
                              >
                                {msg.fromBurrowerId} <Badge colorScheme="purple" fontSize="2xs" ml={1}>Admin</Badge>
                              </Text>
                              <Text 
                                fontSize="xs" 
                                color={!msg.isRead ? "yellow.300" : "gray.500"}
                                flexShrink={0}
                                ml={2}
                              >
                                {formatTime(msg.sentAt)}
                              </Text>
                            </Flex>
                            <Text 
                              fontSize="13px" 
                              color={!msg.isRead ? "gray.200" : "gray.500"}
                              noOfLines={2}
                              fontWeight={!msg.isRead ? "medium" : "normal"}
                            >
                              {msg.adminMessageContents}
                            </Text>
                          </Box>
                        </Flex>
                        );
                      })}
                    </VStack>
                  )}
                </TabPanel>
                
                <TabPanel p={{ base: 0, md: 4 }}>
                  <Text fontSize="lg" fontWeight="medium" mb={4}>Sent Messages</Text>
                  {sentMessages?.length === 0 ? (
                    <Box textAlign="center" p={6} color="gray.500">
                      <EmailIcon boxSize={10} mb={2} />
                      <Text>You haven't sent any messages</Text>
                    </Box>
                  ) : (
                    <VStack align="stretch" spacing={0}>
                      {sentMessages?.map(msg => {
                        const formatTime = (dateString) => {
                          if (!dateString) return '';
                          const date = new Date(dateString);
                          const now = new Date();
                          const seconds = Math.floor((now - date) / 1000);
                          const intervals = { year: 31536000, month: 2592000, week: 604800, day: 86400, hour: 3600, minute: 60, second: 1 };
                          for (const [unit, secondsInUnit] of Object.entries(intervals)) {
                            const counter = Math.floor(seconds / secondsInUnit);
                            if (counter > 0) return counter === 1 ? `1${unit.charAt(0)}` : `${counter}${unit.charAt(0)}`;
                          }
                          return 'now';
                        };
                        return (
                        <Flex 
                          key={msg.sentMessageId} 
                          py={3}
                          px={4}
                          align="center"
                          gap={3}
                          bg="transparent"
                          width="100%"
                          position="relative"
                          _after={{
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "8%",
                            right: "8%",
                            height: "1px",
                            background: "linear-gradient(90deg, transparent, rgba(236,201,75,0.25) 30%, rgba(236,201,75,0.25) 70%, transparent)",
                            boxShadow: "0 1px 4px rgba(236,201,75,0.12)",
                          }}
                        >
                          <Box position="relative" flexShrink={0}>
                            <BurrowAvatar size="md" name={msg.toBurrowerId} />
                          </Box>
                          <Box flex={1} minW={0} overflow="hidden">
                            <Flex align="baseline" justify="space-between" mb={0.5}>
                              <Text 
                                fontWeight="semibold"
                                fontSize="15px"
                                color="white"
                                isTruncated
                              >
                                To: {msg.toBurrowerId}
                              </Text>
                              <Text 
                                fontSize="xs" 
                                color="gray.500"
                                flexShrink={0}
                                ml={2}
                              >
                                {formatTime(msg.sentAt)}
                              </Text>
                            </Flex>
                            <Text 
                              fontSize="13px" 
                              color="gray.500"
                              noOfLines={1}
                            >
                              {msg.messageContents}
                            </Text>
                          </Box>
                        </Flex>
                        );
                      })}
                    </VStack>
                  )}
                </TabPanel>
                
                <TabPanel p={{ base: 0, md: 4 }}>
                  <Box textAlign="center" p={6} color="gray.500">
                    <AddIcon boxSize={10} mb={2} />
                    <Text mb={4}>Create a new message to send to another user</Text>
                    <Button colorScheme="green" onClick={() => sendMessageModal.onOpen()}>
                      New Message
                    </Button>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
        </Box>
      )}

      {/* Message Modals */}
      <UserMessagesModal 
        isOpen={userMessagesModal.isOpen} 
        onClose={userMessagesModal.onClose} 
        messages={userMessages || []} 
        onSendMessage={sendMessageModal.onOpen}
      />
      
      <ConversationModal
        isOpen={conversationModal.isOpen}
        onClose={conversationModal.onClose}
        conversation={selectedConversation}
      />
      
      <AdminMessagesModal 
        isOpen={adminMessagesModal.isOpen} 
        onClose={adminMessagesModal.onClose} 
        messages={adminMessages || []} 
      />
      
      <SentMessagesModal
        isOpen={sentMessagesModal.isOpen}
        onClose={sentMessagesModal.onClose}
        messages={sentMessages || []}
      />
      
      <SendMessageModal 
        isOpen={sendMessageModal.isOpen} 
        onClose={sendMessageModal.onClose} 
      />
    </Box>
  );
};

export default MessagesTab;
